import React from "react";
import { ReconstructSessionResponse } from "../../types/debuggerServiceTypes";
import { ChatMessage } from "../../types/chat";
import MessageItem from "../MessageItem";

interface SessionReconstructionProps {
  session: ReconstructSessionResponse;
  loadMedia: boolean;
}

const SessionReconstruction: React.FC<SessionReconstructionProps> = ({
  session,
  loadMedia,
}) => {
  // Convert reconstructed messages to ChatMessage format for reuse
  const convertToChatMessages = (): ChatMessage[] => {
    return session.messages.map((msg, index) => {
      const chatMessage: ChatMessage = {
        id: `${msg.query_id}-${index}`, // Unique ID for React keys
        messageId: msg.query_id, // Use query_id as messageId for matching
        text: msg.content,
        dateTime: new Date(msg.timestamp),
        origin: msg.origin,
        messageType: msg.role as "bot" | "user",
        nodeUuid: msg.node_uuid,
        options: msg.options || [],
        audio: msg.audio
          ? Array.isArray(msg.audio)
            ? msg.audio
            : [msg.audio]
          : undefined,
        media: msg.media,
      };

      return chatMessage;
    });
  };

  const chatMessages = convertToChatMessages();

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-2 sm:space-y-4 min-h-0">
        {chatMessages.length === 0 ? (
          <div className="text-center text-gray-500 py-4 sm:py-8">
            <svg
              className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h3 className="mt-2 text-xs sm:text-sm font-medium text-gray-900">
              No messages
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              This session contains no messages to display.
            </p>
          </div>
        ) : (
          chatMessages.map((message, index) => {
            // Find the corresponding session message to get endpoint info
            // Use the index to match directly since session messages and chat messages should be in the same order
            const sessionMessage = session.messages[index];

            // Check if this is a bot message and if there's endpoint info to show
            const showEndpointInfo =
              message.messageType === "bot" &&
              sessionMessage?.endpoint &&
              sessionMessage?.endpoint_result;

            return (
              <div key={message.id} className="relative">
                {/* Flagged indicator for flagged bot messages only */}
                {message.messageType === "bot" &&
                  session.messages.find((m) => m.query_id === message.messageId)
                    ?.flagged && (
                    <div className="absolute -top-1 -left-1 sm:-top-2 sm:-left-2 z-10">
                      <span className="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                        🚩 Flagged
                      </span>
                    </div>
                  )}

                {/* User feedback for flagged bot messages only */}
                {message.messageType === "bot" &&
                  session.messages.find((m) => m.query_id === message.messageId)
                    ?.user_feedback && (
                    <div className="mb-2 p-2 sm:p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <p className="text-xs sm:text-sm text-orange-800">
                        <span className="font-medium">User Feedback:</span>{" "}
                        {
                          session.messages.find(
                            (m) => m.query_id === message.messageId
                          )?.user_feedback
                        }
                      </p>
                    </div>
                  )}

                {/* Endpoint and endpoint_result info for bot messages */}
                {showEndpointInfo && (
                  <div className="mb-2 p-1.5 sm:p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                    <div className="font-medium">
                      Endpoint: {sessionMessage.endpoint}
                    </div>
                    <div className="mt-1">
                      {(sessionMessage.endpoint_result || "")
                        .split(";")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <div key={idx}>{line.trim()};</div>
                        ))}
                    </div>
                  </div>
                )}

                {/* MessageItem (normal rendering, will not show media if loadMedia is false) */}
                <MessageItem
                  {...message}
                  loadMedia={loadMedia}
                  isReadOnly={true}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SessionReconstruction;
